import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CourseListItem } from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Badge, EmptyState, LoadingPane, PageHeader, ProgressBar, formatDate } from '../../components/ui.js';
import { CourseArt } from './art.js';
import './training.css';

export function MyCoursesPage() {
  const { org } = useOrg();

  const courses = useQuery({
    queryKey: ['training-courses', org.orgId],
    queryFn: () => apiFetch<{ items: CourseListItem[] }>(`/orgs/${org.orgId}/training/courses`),
  });

  if (courses.isLoading) return <LoadingPane label="Loading courses" />;
  if (courses.isError) {
    return (
      <div>
        <PageHeader title="Courses" />
        <EmptyState
          title="Could not load courses"
          message="Something went wrong fetching the training board — please try again shortly."
        />
      </div>
    );
  }
  const items = courses.data?.items ?? [];

  return (
    <div>
      <PageHeader
        title="Courses"
        subtitle="Use this board to access the compliance and cyber security courses assigned to you. These courses are created by Zest Assure to support your organisation's training requirements. You can track your progress and pick up where you left off at any time."
      />
      {items.length === 0 ? (
        <EmptyState
          title="No courses yet"
          message="Courses appear here once a system administrator has imported the training content."
        />
      ) : (
        <ul className="course-grid" aria-label="Courses">
          {items.map((course) => (
            <li key={course.id}>
              <Link to={`/training/courses/${course.id}`} className="course-card">
                <div className="course-card-media">
                  <CourseArt code={course.code} title={course.title} />
                  <span className="course-duration">{course.durationMinutes} mins</span>
                  {course.progress.expiresAt && !course.progress.expired && (
                    <span className="course-expiry">
                      Expires: {formatDate(course.progress.expiresAt)}
                    </span>
                  )}
                  {course.progress.expired && (
                    <span className="course-expiry course-expiry-lapsed">Expired — retake</span>
                  )}
                </div>
                <div className="course-card-body">
                  <h2 className="course-title">{course.title}</h2>
                  <ProgressBar
                    percent={course.progress.percent}
                    label={`${course.title} progress`}
                  />
                  <div className="course-meta">
                    <span>Complete: {course.progress.percent}%</span>
                    <span>
                      Steps: {course.progress.completedSteps}/{course.progress.steps}
                    </span>
                  </div>
                  {course.progress.status === 'completed' && !course.progress.expired && (
                    <div className="course-badges">
                      <Badge tone="success">Completed</Badge>
                      {course.progress.attested && <Badge tone="info">Attested</Badge>}
                    </div>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
